import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { CallPreset } from '@goaltree/backend/convex/lib/callPresets'
import {
  type CoachShowRequest,
  coachShowTarget,
  matchGoalSection,
} from '@goaltree/backend/convex/lib/coachShow'
import {
  CALL_SERVER_TOOL_NAMES,
  VOICE_COACH_TOOLS,
  callToolOutput,
} from '@goaltree/backend/convex/lib/coachToolDefs'
import { splitHint } from '@goaltree/backend/convex/lib/goalAnalysis'
import { measureProgressText } from '@goaltree/backend/convex/lib/habitMeasure'
import {
  RECURS_PERIODS,
  resolveSchedule,
  scheduleText,
} from '@goaltree/backend/convex/lib/schedule'
import {
  TIME_BUDGET_TOOL_PROPS,
  timeBudgetFromToolArgs,
} from '@goaltree/backend/convex/lib/timeBudget'
import { FREE_HABIT_LIMIT } from '@goaltree/backend/convex/lib/usage'
import { ViewingGate } from '@goaltree/backend/convex/lib/viewingContext'
import { useNavigate } from '@tanstack/react-router'
import { useConvex, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useCoachFace } from '@/components/coach-avatar'
import type { CallAction } from '@/components/coach-call'
import { localDateKey } from '@/hooks/use-coach-chat'
import { useCoachDisplayName } from '@/hooks/use-coach-name'
import { type VoiceTool, useVoiceCall } from '@/hooks/use-voice-call'
import { captureCheckinPhoto } from '@/lib/checkin-capture'
import { useCoachCall } from '@/lib/coach-call-store'
import { dismissFrameAsk, stopSelfCamera } from '@/lib/self-camera'
import { playDoneSound } from '@/lib/sounds'
import { handleUsageLimit, isHabitLimit, planUsageArgs } from '@/lib/usage-limit'

const celebrateDone = playDoneSound

// Function tools the voice agent can call during a live call. Handlers in the
// component dispatch these to the same Convex mutations the text coach uses.
const VOICE_TOOLS: VoiceTool[] = [
  {
    type: 'function',
    name: 'list_goals',
    description: "List the user's active goals with progress. Use to find a goalId.",
    parameters: { type: 'object', properties: {}, required: [] },
  },
  {
    type: 'function',
    name: 'list_goal_tasks',
    description:
      'List the tasks of a goal (with taskIds and done state). Use before completing one.',
    parameters: {
      type: 'object',
      properties: { goalId: { type: 'string', description: 'Goal id from list_goals' } },
      required: ['goalId'],
    },
  },
  {
    type: 'function',
    name: 'create_quick_task',
    description:
      'Create a quick task in the inbox for the user. Set today: true to also put it on their Today list.',
    parameters: {
      type: 'object',
      properties: { title: { type: 'string' }, today: { type: 'boolean' } },
      required: ['title'],
    },
  },
  {
    type: 'function',
    name: 'add_to_today',
    description: 'Put an existing task on the user’s Today list by its id.',
    parameters: {
      type: 'object',
      properties: { taskId: { type: 'string' } },
      required: ['taskId'],
    },
  },
  {
    type: 'function',
    name: 'list_habits',
    description:
      'List the user’s habits with their taskIds, cadence and streak; number habits include today’s total vs target (use log_habit_amount to add to them).',
    parameters: { type: 'object', properties: {}, required: [] },
  },
  {
    type: 'function',
    name: 'create_habit',
    description: 'Create a recurring habit (defaults to daily).',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string' },
        cadence: {
          type: 'string',
          enum: ['daily', 'everyOtherDay', 'weekly', 'monthly', 'weekend', 'weekday', 'days'],
        },
        days: {
          type: 'array',
          items: { type: 'integer', minimum: 0, maximum: 6 },
          description:
            'Only with cadence "days": days of the week, 0=Sun … 6=Sat (Mon/Wed/Fri → [1,3,5]).',
        },
      },
      required: ['title'],
    },
  },
  {
    type: 'function',
    name: 'mark_task_done',
    description:
      'Mark a task, habit, or quick task complete by its id. Call this ONLY when the user explicitly says they finished it ("done", "I did it"). Picking, choosing or agreeing to start a task ("the first one", "ok", "let\'s do that") is NOT completion.',
    parameters: {
      type: 'object',
      properties: { taskId: { type: 'string' } },
      required: ['taskId'],
    },
  },
  {
    type: 'function',
    name: 'mark_task_not_done',
    description:
      'Undo a completion: mark a task, habit or quick task as not done again. Use when the user says it was marked done by mistake or they did not actually do it.',
    parameters: {
      type: 'object',
      properties: { taskId: { type: 'string' } },
      required: ['taskId'],
    },
  },
  {
    type: 'function',
    name: 'set_task_deadline',
    description: 'Set a task deadline. dueDateMs is a Unix epoch in milliseconds.',
    parameters: {
      type: 'object',
      properties: { taskId: { type: 'string' }, dueDateMs: { type: 'number' } },
      required: ['taskId', 'dueDateMs'],
    },
  },
  {
    type: 'function',
    name: 'create_goal',
    description:
      'Create a brand-new goal, fully broken down into milestones, targets and tasks. This takes a few seconds while the plan is generated — ALWAYS say a short line out loud that you are building it now BEFORE calling this, then confirm briefly once it returns. Optional tasks[] seeds the plan with steps the user already mentioned.',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'The goal stated concisely' },
        effort: { type: 'string', enum: ['low', 'medium', 'high'] },
        tasks: { type: 'array', items: { type: 'string' } },
        ...TIME_BUDGET_TOOL_PROPS,
      },
      required: ['title'],
    },
  },
  ...VOICE_COACH_TOOLS,
]

export type CoachCallSummaryState = {
  seconds: number
  since: number
  keyPoints?: string[]
  preset?: CallPreset
}
type StartOpts = Parameters<ReturnType<typeof useVoiceCall>['start']>[0]

// The one live coach call, owned by CoachCallHost at the app root so it keeps
// going while the sheet is closed or the user navigates (the PiP card).
function glowTodayTasks(ids: string[]) {
  if (typeof document === 'undefined' || ids.length === 0) return
  let tries = 0
  const tick = () => {
    let pending = false
    for (const id of ids) {
      const el = document.querySelector(`[data-today-task-id="${CSS.escape(id)}"]`)
      if (!el) {
        pending = true
        continue
      }
      el.classList.add('task-highlight')
      window.setTimeout(() => el.classList.remove('task-highlight'), 2800)
    }
    if (pending && ++tries < 30) window.setTimeout(tick, 100)
  }
  window.setTimeout(tick, 250)
}

export function useCoachCallSession() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const convex = useConvex()
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | {
        coachVoice?: string
        coachName?: string
        callVideo?: boolean
        callQuick?: boolean
        coachShowInApp?: boolean
        coachSeeScreen?: boolean
      }
    | undefined
  const coachFaceOn = !!useCoachFace(prefs?.coachVoice)
  const coachName = useCoachDisplayName(prefs)
  const [callActions, setCallActions] = useState<CallAction[]>([])
  const [callSummary, setCallSummary] = useState<CoachCallSummaryState | null>(null)
  const callPresetRef = useRef<CallPreset | undefined>(undefined)
  const transcriptSaves = useRef<Set<Promise<unknown>>>(new Set())
  const callUpdatedChat = useRef(false)
  // Where this call's turns start in the chat history, for the live transcript.
  const callHistoryStart = useRef(0)
  const hungUpFromPip = useRef(false)
  const coachShownAt = useRef(Number.NEGATIVE_INFINITY)

  // Each change shows as an undoable card in the call and is saved to the chat
  // as its own note, so it's there in the transcript and after the call.
  const callNoteIds = useRef(new Map<string, Promise<Id<'coachMessages'> | null>>())
  function recordAction(
    action: Omit<CallAction, 'id'>,
    note: {
      type: string
      taskId?: Id<'nodes'>
      commitmentId?: Id<'coachCommitments'>
      undo?: 'complete' | 'uncomplete' | 'remove' | 'commitment'
      values?: Record<string, string | number>
    },
  ) {
    callUpdatedChat.current = true
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    setCallActions((list) => [...list, { ...action, id }])
    const saved = convex
      .mutation(api.coachHistory.addCallAction, {
        action: {
          type: note.type,
          description:
            action.kind === 'commitment'
              ? action.title
              : t(`callView.note.${action.kind}`, { title: action.title, ...note.values }),
          ...(note.taskId ? { taskId: note.taskId } : {}),
          ...(note.commitmentId ? { commitmentId: note.commitmentId } : {}),
          ...(note.undo ? { undo: note.undo } : {}),
        },
      })
      .catch(() => null)
    callNoteIds.current.set(id, saved)
  }

  function undoCallAction(id: string) {
    const action = callActions.find((a) => a.id === id)
    if (!action?.undo || action.undone) return
    setCallActions((list) => list.map((a) => (a.id === id ? { ...a, undone: true } : a)))
    void action
      .undo()
      .then(async () => {
        const messageId = await callNoteIds.current.get(id)
        if (messageId)
          await convex.mutation(api.coachHistory.markActionUndone, { messageId, index: 0 })
      })
      .catch(() => {
        setCallActions((list) => list.map((a) => (a.id === id ? { ...a, undone: false } : a)))
        toast.error(t('callView.undoError'))
      })
  }

  const nodeBrief = (nodeId: Id<'nodes'>) =>
    convex.query(api.voiceCall.nodeBrief, { nodeId }).catch(() => null)

  const removeNode = (nodeId: Id<'nodes'>) => convex.mutation(api.nodes.remove, { nodeId })

  async function recordSharedTool(name: string, args: Record<string, unknown>, result: string) {
    if (name === 'freeze_goal' || name === 'unfreeze_goal') {
      if (!/^(Froze|Unfroze) /.test(result)) return
      const goalId = args.goal_id as Id<'nodes'>
      const brief = await nodeBrief(goalId)
      const freezing = name === 'freeze_goal'
      recordAction(
        {
          kind: freezing ? 'freeze' : 'unfreeze',
          title: brief?.title ?? '',
          undo: () =>
            freezing
              ? convex.mutation(api.nodes.unfreezeGoal, { goalId })
              : convex.mutation(api.nodes.freezeGoal, { goalId }),
        },
        { type: name },
      )
    } else if (name === 'track_commitment') {
      const m = /^Saved commitment \[([^\]]+)\]: "(.*)" — /.exec(result)
      if (!m) return
      const commitmentId = m[1] as Id<'coachCommitments'>
      recordAction(
        {
          kind: 'commitment',
          title: m[2],
          undo: () => convex.mutation(api.coachCommitments.remove, { id: commitmentId }),
        },
        { type: 'commitment', commitmentId, undo: 'commitment' },
      )
    } else if (name === 'send_grove_message' && result === 'Posted.') {
      recordAction({ kind: 'grove', title: String(args.message ?? '') }, { type: name })
    } else if (name === 'add_tasks_to_goal' || name === 'move_tasks_to_goal') {
      const n = Number(/^(?:Added|Moved) (\d+) /.exec(result)?.[1] ?? 0)
      if (!n) return
      const goal = await nodeBrief(args.goal_id as Id<'nodes'>)
      const ids = [...result.matchAll(/^• \[([^\]]+)\]/gm)].map((m) => m[1] as Id<'nodes'>)
      const count = t('inbox.taskCount', { count: n })
      recordAction(
        name === 'add_tasks_to_goal'
          ? {
              kind: 'goalTask',
              title: goal?.title ?? '',
              context: count,
              undo: () => Promise.all(ids.map(removeNode)),
            }
          : { kind: 'move', title: goal?.title ?? '', context: count },
        { type: name === 'add_tasks_to_goal' ? 'add_to_goal' : 'move_tasks', values: { count } },
      )
    } else if (name === 'log_habit_amount') {
      const m = /^Logged (\S+) (.*?) to "/.exec(result)
      if (!m) return
      const brief = await nodeBrief(args.habit_id as Id<'nodes'>)
      const amount = `+${m[1]} ${m[2]}`.trim()
      recordAction(
        { kind: 'logged', title: brief?.title ?? '', context: amount },
        { type: name, values: { amount } },
      )
    }
  }

  async function handleToolCall(name: string, args: Record<string, unknown>) {
    if (CALL_SERVER_TOOL_NAMES.has(name)) {
      const result = await convex.action(api.coachTools.run, { name, args })
      await recordSharedTool(name, args, result)
      if (
        name === 'start_habit_timer' &&
        typeof result === 'string' &&
        result.includes('Started ') &&
        typeof args.habit_id === 'string'
      ) {
        useCoachCall.getState().minimize()
        void navigate({
          to: '/everyday',
          search: { tab: 'habits', focusTask: args.habit_id, openTask: args.habit_id },
        })
      }
      return callToolOutput(result)
    }
    switch (name) {
      case 'list_goals': {
        const goals = await convex.query(api.nodes.getUserGoals, {})
        return goals
          .filter((g) => !g.isInbox && g.status !== 'done')
          .map((g) => ({
            id: g._id,
            title: g.title,
            progress: Math.round(g.progress),
            remaining: g.remainingCount,
          }))
      }
      case 'list_goal_tasks': {
        const tree = await convex.query(api.nodes.getPublicGoalTree, {
          goalId: args.goalId as Id<'nodes'>,
        })
        if (!tree) return { error: 'goal not found' }
        const tasks = tree.milestones
          .flatMap((m) => m.targets.flatMap((tg) => tg.tasks))
          .map((tk) => ({ id: tk._id, title: tk.title, done: tk.completedAt != null }))
        return { goal: tree.title, tasks }
      }
      case 'create_quick_task': {
        const title = String(args.title)
        const id = await convex.mutation(api.nodes.createQuickTask, { title })
        const today = args.today === true
        if (today)
          await convex.mutation(api.dailySurface.addToToday, {
            taskId: id,
            localDate: localDateKey(),
          })
        recordAction(
          { kind: today ? 'today' : 'task', title, undo: () => removeNode(id) },
          { type: 'create_task', taskId: id, undo: 'remove' },
        )
        return { id, ok: true }
      }
      case 'add_to_today': {
        const taskId = args.taskId as Id<'nodes'>
        const localDate = localDateKey()
        const card = await convex.mutation(api.voiceCall.addTaskToToday, { taskId, localDate })
        if (card.already) return { ok: true, alreadyOnToday: true }
        recordAction(
          {
            kind: 'today',
            title: card.title,
            context: [card.goalTitle, card.targetTitle].filter(Boolean).join(' · ') || undefined,
            undo: () => convex.mutation(api.dailySurface.removeFromToday, { taskId, localDate }),
          },
          { type: 'add_to_today', taskId },
        )
        return { ok: true }
      }
      case 'list_habits': {
        const habits = await convex.query(api.nodes.getHabits, {})
        return habits.map((h) => ({
          id: h._id,
          title: h.title,
          cadence: scheduleText(h.recursPeriod, h.recursDays),
          streak: h.streakDays ?? 0,
          ...(h.measure && {
            number: measureProgressText(
              h.measure,
              h.measureToday?.total ?? 0,
              h.measureToday?.count ?? 0,
            ),
          }),
        }))
      }
      case 'create_habit': {
        const schedule = resolveSchedule(
          RECURS_PERIODS.find((c) => c === args.cadence),
          Array.isArray(args.days) ? args.days : undefined,
        )
        const title = String(args.title)
        let id: Id<'nodes'>
        try {
          id = await convex.mutation(api.nodes.createQuickTask, {
            title,
            ...schedule,
            isHabit: true,
          })
        } catch (e) {
          if (!isHabitLimit(e)) throw e
          handleUsageLimit(e)
          return {
            error: `Free plan is limited to ${FREE_HABIT_LIMIT} active habits — ask them to upgrade to Pro.`,
          }
        }
        recordAction(
          { kind: 'habit', title, undo: () => removeNode(id) },
          { type: 'create_habit', taskId: id, undo: 'remove' },
        )
        return { id, ok: true }
      }
      case 'mark_task_done': {
        const taskId = args.taskId as Id<'nodes'>
        await convex.mutation(api.nodes.complete, { taskId })
        celebrateDone()
        const brief = await nodeBrief(taskId)
        recordAction(
          {
            kind: 'done',
            title: brief?.title ?? '',
            undo: () => convex.mutation(api.nodes.uncomplete, { taskId }),
          },
          { type: 'mark_done', taskId, undo: 'uncomplete' },
        )
        return { ok: true }
      }
      case 'mark_task_not_done': {
        const taskId = args.taskId as Id<'nodes'>
        await convex.mutation(api.nodes.uncomplete, { taskId })
        const brief = await nodeBrief(taskId)
        recordAction(
          {
            kind: 'undone',
            title: brief?.title ?? '',
            undo: () => convex.mutation(api.nodes.complete, { taskId }),
          },
          { type: 'mark_not_done', taskId, undo: 'complete' },
        )
        return { ok: true }
      }
      case 'set_task_deadline': {
        const taskId = args.taskId as Id<'nodes'>
        const before = await nodeBrief(taskId)
        const dueDate = Number(args.dueDateMs)
        await convex.mutation(api.nodes.updateTaskDueDate, { taskId, dueDate })
        const when = new Date(dueDate).toLocaleDateString(i18n.language, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
        recordAction(
          {
            kind: 'deadline',
            title: before?.title ?? '',
            context: when,
            undo: () =>
              convex.mutation(api.nodes.updateTaskDueDate, { taskId, dueDate: before?.dueDate }),
          },
          { type: 'set_deadline', taskId, values: { when } },
        )
        return { ok: true }
      }
      case 'create_goal': {
        const seedTasks = Array.isArray(args.tasks)
          ? (args.tasks as unknown[]).filter((s): s is string => typeof s === 'string')
          : undefined
        const effort =
          (['low', 'medium', 'high'] as const).find((e) => e === args.effort) ?? 'medium'
        const timeBudget = timeBudgetFromToolArgs(args)
        // Never blocks: only adds a note when the request looked like several goals.
        const analysis = convex
          .action(api.onboarding.analyzeGoal, {
            goalText: String(args.title),
            locale: i18n.language,
            ...planUsageArgs(),
          })
          .catch(() => null)
        try {
          const tree = await convex.action(api.onboarding.decomposeGoal, {
            goalText: String(args.title),
            experienceLevel: 'Intermediate',
            locale: i18n.language,
            effortLevel: effort,
            ...planUsageArgs(),
            ...(timeBudget && { timeBudget }),
            ...(seedTasks && seedTasks.length ? { seedTasks } : {}),
          })
          const goalId = await convex.mutation(api.nodes.saveFromAnonymous, { tree })
          recordAction(
            { kind: 'goal', title: tree.title, undo: () => removeNode(goalId as Id<'nodes'>) },
            { type: 'create_goal', taskId: goalId as Id<'nodes'>, undo: 'remove' },
          )
          const note = splitHint((await analysis)?.goals ?? [])
          return {
            goalId,
            title: tree.title,
            milestones: tree.milestones.length,
            ...(note && { note }),
          }
        } catch (e) {
          if (handleUsageLimit(e))
            return { error: "They've hit today's plan-building limit — it resets tomorrow." }
          const data =
            e instanceof ConvexError ? String(e.data) : e instanceof Error ? e.message : ''
          return {
            error: data.includes('Free plan is limited')
              ? 'Free plan is limited to 3 active goals — ask them to upgrade to Pro.'
              : 'Could not create the goal',
          }
        }
      }
      default:
        return { error: 'unknown tool' }
    }
  }
  async function showInApp(req: CoachShowRequest) {
    let sectionId: string | undefined
    if (req.surface === 'goal' && req.goalId) {
      const tree = await convex
        .query(api.nodes.getGoalTree, { goalId: req.goalId })
        .catch(() => null)
      if (!tree) return { opened: false, reason: 'goal not found' }
      if (req.section) sectionId = matchGoalSection(tree.milestones, req.section)
    }
    const target = coachShowTarget(req, sectionId)
    coachShownAt.current = Date.now()
    useCoachCall.getState().minimize()
    switch (target.kind) {
      case 'goal':
        void navigate({
          to: '/goals/$goalId',
          params: { goalId: target.goalId },
          search: {
            focusNode: target.focus,
            openMilestone: undefined,
            focusTask: undefined,
            addTask: undefined,
          },
        })
        return { opened: true, where: 'goal', section: target.focus ?? null }
      case 'habit':
        void navigate({
          to: '/everyday',
          search: { tab: 'habits', focusTask: target.habitId, openTask: target.habitId },
        })
        return { opened: true, where: 'habit' }
      case 'today':
        void navigate({ to: '/today' })
        glowTodayTasks(target.taskIds)
        return { opened: true, where: 'today' }
      case 'stats':
        void navigate({ to: '/stats' })
        return { opened: true, where: 'stats' }
      case 'everyday':
        void navigate({
          to: '/everyday',
          search: { tab: target.tab, focusTask: undefined, openTask: undefined },
        })
        return { opened: true, where: target.tab }
      case 'templates':
        if (target.templateId)
          void navigate({
            to: '/templates/$templateId',
            params: { templateId: target.templateId },
          })
        else
          void navigate({
            to: '/explore',
            search: { tab: 'templates', ...(target.tab ? { templatesKind: target.tab } : {}) },
          })
        return { opened: true, where: 'templates' }
    }
  }

  const voiceCall = useVoiceCall({
    tools: VOICE_TOOLS,
    onToolCall: handleToolCall,
    openScreens: prefs?.coachShowInApp !== false,
    onShow: showInApp,
    realistic: coachFaceOn,
    video: prefs?.callVideo !== false,
    quick: prefs?.callQuick === true,
    // Save each spoken turn into chat history so the call shows up afterwards.
    onTranscript: (role, text, atMs, itemId) => {
      if (role === 'assistant') callUpdatedChat.current = true
      const save = convex
        .mutation(api.coachHistory.addVoiceMessage, {
          role,
          content: text,
          atMs,
          ...(itemId ? { callItemId: itemId } : {}),
        })
        .catch(() => {})
      transcriptSaves.current.add(save)
      void save.finally(() => transcriptSaves.current.delete(save))
    },
    onCaptureCheckin: async (habitId) => {
      const result = await captureCheckinPhoto(convex, habitId)
      if (result.saved && result.checkedIn && !result.alreadyDone) celebrateDone()
      if (result.saved && result.habit) {
        const taskId = habitId as Id<'nodes'>
        const date = localDateKey()
        recordAction(
          {
            kind: 'photo',
            title: result.habit,
            undo: async () => {
              if (result.checkedIn && !result.alreadyDone)
                await convex.mutation(api.nodes.uncomplete, { taskId })
              await convex.mutation(api.habitPhotos.remove, { habitId: taskId, date })
            },
          },
          { type: 'photo', taskId },
        )
      }
      return result
    },
    onEnded: ({ startedAt, seconds }) => {
      stopSelfCamera()
      dismissFrameAsk()
      const st = useCoachCall.getState()
      const fromPip = st.mode === 'pip'
      const byUser = hungUpFromPip.current
      hungUpFromPip.current = false
      if (st.fullscreen || fromPip) {
        st.setFullscreen(false)
        // A fullscreen call always ends in the coach sheet, wherever it was started.
        if (!callUpdatedChat.current) {
          if (fromPip) useCoachCall.setState({ mode: 'full' })
          return
        }
      }
      const preset = callPresetRef.current
      setCallSummary({ seconds, since: startedAt, preset })
      if (fromPip) {
        if (byUser) st.expand()
        else {
          useCoachCall.setState({ mode: 'full' })
          toast(t('callView.pip.ended', { name: coachName }), {
            action: { label: t('callView.pip.viewSummary'), onClick: () => st.expand() },
          })
        }
      }
      // The recap reads the saved turns, so let the last saves land first.
      void Promise.allSettled(transcriptSaves.current)
        .then(() => convex.action(api.voiceCallSummary.summarize, { since: startedAt, preset }))
        .then(
          ({ keyPoints }) => setCallSummary((s) => s && { ...s, keyPoints }),
          () => setCallSummary((s) => s && { ...s, keyPoints: [] }),
        )
    },
  })
  const callActive = voiceCall.status === 'connecting' || voiceCall.status === 'live'
  const setActive = useCoachCall((s) => s.setActive)
  useEffect(() => setActive(callActive), [callActive, setActive])
  // The coach hears which screen is open unless Settings turns that off.
  useEffect(() => {
    useCoachCall.setState({ shareViewing: prefs?.coachSeeScreen !== false })
  }, [prefs?.coachSeeScreen])
  useViewingFeed(voiceCall, coachShownAt)

  // A call that fails or drops before going live leaves the fullscreen/PiP view.
  // Only a status change counts: the previous call's 'ended' is still the status
  // when the next one starts.
  const prevCallStatus = useRef(voiceCall.status)
  useEffect(() => {
    const changed = prevCallStatus.current !== voiceCall.status
    prevCallStatus.current = voiceCall.status
    if (!changed || !['ended', 'error'].includes(voiceCall.status)) return
    const st = useCoachCall.getState()
    if (st.mode === 'pip') useCoachCall.setState({ mode: 'full' })
    if (st.fullscreen) st.setFullscreen(false)
  }, [voiceCall.status])

  async function begin(opts: StartOpts, historyStart: number) {
    if (callActive) return
    setCallActions([])
    setCallSummary(null)
    callUpdatedChat.current = false
    callHistoryStart.current = historyStart
    callPresetRef.current = opts?.preset
    await voiceCall.start(opts)
  }

  return {
    voiceCall,
    callActive,
    callActions,
    undoCallAction,
    callSummary,
    setCallSummary,
    callUpdatedChat,
    callHistoryStart,
    transcriptSaves,
    coachName,
    coachVoice: prefs?.coachVoice,
    coachFaceOn,
    begin,
    clear: () => {
      setCallSummary(null)
      setCallActions([])
    },
    hangUpFromPip: () => {
      hungUpFromPip.current = true
      voiceCall.end()
    },
  }
}
export type CoachCallSession = ReturnType<typeof useCoachCallSession>

const VIEWING_POLL_MS = 400

// Feeds what's open on screen to the live coach through ViewingGate. Whatever
// was already open when the call went live is shared quietly, without a reply.
function useViewingFeed(
  call: ReturnType<typeof useVoiceCall>,
  coachShownAt: { readonly current: number },
) {
  const callRef = useRef(call)
  callRef.current = call
  const live = call.status === 'live'
  useEffect(() => {
    if (!live) return
    const gate = new ViewingGate()
    const top = () => {
      const st = useCoachCall.getState()
      return st.shareViewing ? (st.viewing.at(-1) ?? null) : null
    }
    const startKey = top()?.key
    gate.offer(top(), Date.now())
    const unsub = useCoachCall.subscribe(() => gate.offer(top(), Date.now()))
    const id = setInterval(() => {
      const c = callRef.current
      const now = Date.now()
      gate.coachShowed(coachShownAt.current)
      const out = gate.poll(now, c.contextState())
      if (!out) return
      const quiet = top()?.key === startKey
      if (c.sendContext(out.text, out.respond && !quiet)) gate.sent(now)
    }, VIEWING_POLL_MS)
    return () => {
      clearInterval(id)
      unsub()
    }
  }, [live])
}
